<script>
// Shared day-by-day weekly hours grid, extracted from OpeningHoursModal so
// ScheduleModal (Layer 3) can reuse it for `working_hours`. `day_of_week`:
// 0 = Monday … 6 = Sunday (docs/real-estate/be/10-api-reference.md §0.6).
// Several shifts on one day make a split shift; a day with no shifts is
// closed.
//
// `value`/`input` form a v-model over the flat
// [{day_of_week,start_time,end_time}] array the BE expects. Local `days`
// state is only seeded from `value` once (in `data()`) — like the modal it
// was extracted from, this component is always mounted fresh (parents use
// `v-if`) rather than kept alive across a prop swap, so there's no need to
// re-sync from an external `value` change, and doing so would risk
// clobbering an in-progress shift that's missing a start or end time (see
// `flatten` below, which drops incomplete shifts).
const DAY_LABELS = [
  'REAL_ESTATE.OPENING_HOURS.MONDAY',
  'REAL_ESTATE.OPENING_HOURS.TUESDAY',
  'REAL_ESTATE.OPENING_HOURS.WEDNESDAY',
  'REAL_ESTATE.OPENING_HOURS.THURSDAY',
  'REAL_ESTATE.OPENING_HOURS.FRIDAY',
  'REAL_ESTATE.OPENING_HOURS.SATURDAY',
  'REAL_ESTATE.OPENING_HOURS.SUNDAY',
];

function groupByDay(hours) {
  return DAY_LABELS.map((labelKey, dayOfWeek) => ({
    dayOfWeek,
    labelKey,
    shifts: hours
      .filter(row => row.day_of_week === dayOfWeek)
      .map(row => ({ start_time: row.start_time, end_time: row.end_time })),
  }));
}

function flatten(days) {
  return days.flatMap(day =>
    day.shifts
      .filter(s => s.start_time && s.end_time)
      .map(s => ({
        day_of_week: day.dayOfWeek,
        start_time: s.start_time,
        end_time: s.end_time,
      }))
  );
}

export default {
  name: 'WeeklyHoursEditor',
  props: {
    value: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      days: groupByDay(this.value),
    };
  },
  methods: {
    emitChange() {
      this.$emit('input', flatten(this.days));
    },
    // A day's first shift pre-fills a typical 9 AM – 5 PM. Later (split)
    // shifts start blank — a second 9–5 would just trip overlapping_hours.
    addShift(day) {
      if (day.shifts.length) {
        day.shifts.push({ start_time: '', end_time: '' });
      } else {
        day.shifts.push({ start_time: '09:00', end_time: '17:00' });
      }
      this.emitChange();
    },
    removeShift(day, index) {
      day.shifts.splice(index, 1);
      this.emitChange();
    },
    isShiftInvalid(shift) {
      return (
        shift.start_time && shift.end_time && shift.end_time <= shift.start_time
      );
    },
    // Exposed for the parent (via $refs) to block submission without
    // duplicating the per-shift check.
    isValid() {
      return !this.days.some(day => day.shifts.some(this.isShiftInvalid));
    },
  },
};
</script>

<template>
  <div class="space-y-3">
    <div
      v-for="day in days"
      :key="day.dayOfWeek"
      class="border border-slate-200 dark:border-slate-700 rounded-lg p-3"
    >
      <div class="flex items-center justify-between mb-2">
        <p class="text-sm font-medium text-slate-900 dark:text-slate-100">
          {{ $t(day.labelKey) }}
        </p>
        <woot-button
          type="button"
          variant="clear"
          size="small"
          icon="add"
          @click="addShift(day)"
        >
          {{ $t('REAL_ESTATE.OPENING_HOURS.ADD_SHIFT') }}
        </woot-button>
      </div>

      <p v-if="!day.shifts.length" class="text-xs text-slate-400">
        {{ $t('REAL_ESTATE.OPENING_HOURS.CLOSED') }}
      </p>

      <div
        v-for="(shift, index) in day.shifts"
        :key="index"
        class="flex items-center gap-2 mb-2"
      >
        <input
          v-model="shift.start_time"
          type="time"
          class="bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg py-1.5 px-2 text-sm text-slate-900 dark:text-slate-100"
          @change="emitChange"
        />
        <span class="text-slate-400">–</span>
        <input
          v-model="shift.end_time"
          type="time"
          class="bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg py-1.5 px-2 text-sm text-slate-900 dark:text-slate-100"
          @change="emitChange"
        />
        <woot-button
          type="button"
          variant="clear"
          size="small"
          color-scheme="alert"
          icon="delete"
          @click="removeShift(day, index)"
        />
        <span v-if="isShiftInvalid(shift)" class="text-xs text-red-500">
          {{ $t('REAL_ESTATE.ERRORS.INVALID_TIME_RANGE') }}
        </span>
      </div>
    </div>
  </div>
</template>
